import * as React from "react"
import { useState } from "react"
import Select from "@imminently/decisively-ui/Select"
import MultiSelect from "@imminently/decisively-ui/MultiSelect"
import Icon from "@imminently/decisively-ui/Icon"
import { Label } from "@/ui/primitives/label"
import Switch from "@imminently/decisively-ui/Switch"

import {
  ComponentPage,
  ComponentSection,
  ComponentPreview,
  ControlPanel,
  ControlRow,
} from "../components/ComponentPage"

export function SelectPage() {
  const [value, setValue] = useState("")
  const [longListValue, setLongListValue] = useState("")
  const [closeOnSelectValue, setCloseOnSelectValue] = useState("")
  const [stayOpenOnSelectValue, setStayOpenOnSelectValue] = useState("")
  const [multiSelectValue, setMultiSelectValue] = useState<string[]>([])
  const [disabled, setDisabled] = useState(false)
  const longListOptions = React.useMemo(
    () =>
      Array.from({ length: 500 }, (_, i) => ({
        value: `item-${i + 1}`,
        label: `Long List Item ${i + 1}`,
      })),
    [],
  )

  return (
    <ComponentPage
      title="Select"
      description="Displays a list of options for the user to pick from—triggered by a button."
    >
      {/* Interactive Playground */}
      <ComponentSection
        title="Playground"
        description="Experiment with select configurations"
      >
        <div className="grid gap-6 lg:grid-cols-2">
          <ComponentPreview>
            <Select
              value={value}
              onValueChange={(v) => setValue(v ?? "")}
              disabled={disabled}
              options={[
                { value: "apple", label: "Apple" },
                { value: "banana", label: "Banana" },
                { value: "cherry", label: "Cherry" },
                { value: "date", label: "Date" },
                { value: "elderberry", label: "Elderberry" },
              ]}
              placeholder="Select a fruit"
            />

            <Select
              clearable={true}
              value={value}
              onValueChange={(v) => setValue(v ?? "")}
              disabled={disabled}
              options={[
                { value: "apple", label: "Apple" },
                { value: "banana", label: "Banana" },
                { value: "cherry", label: "Cherry" },
                { value: "date", label: "Date" },
                { value: "elderberry", label: "Elderberry" },
              ]}
              placeholder="Clearable"
            />
          </ComponentPreview>

          <ControlPanel>
            <ControlRow label="Disabled">
              <Switch checked={disabled} onCheckedChange={setDisabled} />
            </ControlRow>
            <ControlRow label="Value">
              <span className="text-sm text-muted-foreground">
                {value || "(none)"}
              </span>
            </ControlRow>
          </ControlPanel>
        </div>
      </ComponentSection>

      {/* Default */}
      <ComponentSection title="Default" description="Basic select component">
        <ComponentPreview>
          <Select
            options={[
              { value: "option1", label: "Option 1" },
              { value: "option2", label: "Option 2" },
              { value: "option3", label: "Option 3" },
            ]}
            placeholder="Select option"
          />
        </ComponentPreview>
      </ComponentSection>

      {/* With Label */}
      <ComponentSection title="With Label" description="Select with associated label">
        <ComponentPreview>
          <div className="grid gap-1.5">
            <Label htmlFor="country">Country</Label>
            <Select
              options={[
                { value: "us", label: "United States" },
                { value: "uk", label: "United Kingdom" },
                { value: "ca", label: "Canada" },
                { value: "au", label: "Australia" },
              ]}
              placeholder="Select country"
            />
          </div>
        </ComponentPreview>
      </ComponentSection>

      {/* With Groups */}
      <ComponentSection title="With Groups" description="Grouped select options">
        <ComponentPreview>
          <Select
            options={[
              {
                label: "North America",
                options: [
                  { value: "est", label: "Eastern Standard Time (EST)" },
                  { value: "cst", label: "Central Standard Time (CST)" },
                  { value: "mst", label: "Mountain Standard Time (MST)" },
                  { value: "pst", label: "Pacific Standard Time (PST)" },
                ],
              },
              {
                label: "Europe & Africa",
                options: [
                  { value: "gmt", label: "Greenwich Mean Time (GMT)" },
                  { value: "cet", label: "Central European Time (CET)" },
                  { value: "eet", label: "Eastern European Time (EET)" },
                ],
              },
              {
                label: "Asia",
                options: [
                  { value: "ist", label: "India Standard Time (IST)" },
                  { value: "cst-cn", label: "China Standard Time (CST)" },
                  { value: "jst", label: "Japan Standard Time (JST)" },
                ],
              },
            ]}
            placeholder="Select a timezone"
          />
        </ComponentPreview>
      </ComponentSection>

      {/* States */}
      <ComponentSection title="States" description="Different select states">
        <ComponentPreview className="flex-wrap gap-4">
          <div className="space-y-1.5">
            <Label>Default</Label>
            <Select
              options={[
                { value: "1", label: "Option 1" },
                { value: "2", label: "Option 2" },
              ]}
              placeholder="Select..."
            />
          </div>
          <div className="space-y-1.5">
            <Label>Disabled</Label>
            <Select
              disabled
              options={[
                { value: "1", label: "Option 1" },
                { value: "2", label: "Option 2" },
              ]}
              placeholder="Select..."
            />
          </div>
          <div className="space-y-1.5">
            <Label>With Value</Label>
            <Select
              value="selected"
              options={[
                { value: "selected", label: "Selected Option" },
                { value: "other", label: "Other Option" },
              ]}
            />
          </div>
        </ComponentPreview>
      </ComponentSection>

      <ComponentSection
        title="Start Adornment"
        description="Select with a leading icon in the trigger."
      >
        <ComponentPreview>
          <div className="grid gap-1.5 w-full max-w-sm">
            <Label>Interview Select</Label>
            <Select
              options={[
                { value: "intake", label: "Intake Interview" },
                { value: "eligibility", label: "Eligibility Interview" },
                { value: "review", label: "Review Interview" },
              ]}
              placeholder="Select interview"
              startAdornment={<Icon name="interview" />}
              fullWidth
            />
          </div>
        </ComponentPreview>
      </ComponentSection>

      {/* Disabled Items */}
      <ComponentSection title="Disabled Items" description="Select with some disabled options">
        <ComponentPreview>
          <Select
            options={[
              { value: "free", label: "Free" },
              { value: "pro", label: "Pro" },
              { value: "enterprise", label: "Enterprise (Coming Soon)", isDisabled: true },
            ]}
            placeholder="Select plan"
          />
        </ComponentPreview>
      </ComponentSection>

      <ComponentSection
        title="Long List"
        description="Large option set to verify virtualized menu behavior."
      >
        <ComponentPreview>
          <div className="grid gap-1.5 w-full max-w-sm">
            <Label>Large dataset (500 items)</Label>
            <Select
              value={longListValue}
              onValueChange={(v) => setLongListValue(v ?? "")}
              options={longListOptions}
              placeholder="Select from long list"
              fullWidth
            />
          </div>
        </ComponentPreview>
      </ComponentSection>

      <ComponentSection
        title="Close Menu On Select"
        description="Compare dropdown behavior when selecting an option."
      >
        <ComponentPreview className="flex-wrap gap-4">
          <div className="space-y-1.5 w-full max-w-sm">
            <Label>closeMenuOnSelect=true</Label>
            <Select
              value={closeOnSelectValue}
              onValueChange={(v) => setCloseOnSelectValue(v ?? "")}
              options={[
                { value: "one", label: "Option One" },
                { value: "two", label: "Option Two" },
                { value: "three", label: "Option Three" },
              ]}
              placeholder="Select and close"
              closeMenuOnSelect={true}
              fullWidth
            />
          </div>
          <div className="space-y-1.5 w-full max-w-sm">
            <Label>closeMenuOnSelect=false</Label>
            <Select
              value={stayOpenOnSelectValue}
              onValueChange={(v) => setStayOpenOnSelectValue(v ?? "")}
              options={[
                { value: "one", label: "Option One" },
                { value: "two", label: "Option Two" },
                { value: "three", label: "Option Three" },
              ]}
              placeholder="Select and keep open"
              closeMenuOnSelect={false}
              fullWidth
            />
          </div>
          <div className="space-y-1.5 w-full max-w-sm">
            <Label>MultiSelect example</Label>
            <MultiSelect
              value={multiSelectValue}
              onValueChange={(v) => setMultiSelectValue((v as string[]) ?? [])}
              options={[
                { value: "alpha", label: "Alpha" },
                { value: "beta", label: "Beta" },
                { value: "gamma", label: "Gamma" },
                { value: "delta", label: "Delta" },
              ]}
              placeholder="Pick multiple"
              fullWidth
            />
          </div>
        </ComponentPreview>
      </ComponentSection>

      {/* Form Example */}
      <ComponentSection title="Form Example" description="Selects in a form context">
        <ComponentPreview>
          <form className="space-y-4 w-full max-w-sm">
            <div className="grid gap-1.5">
              <Label htmlFor="form-role">Role</Label>
              <Select
                options={[
                  { value: "admin", label: "Administrator" },
                  { value: "editor", label: "Editor" },
                  { value: "viewer", label: "Viewer" },
                ]}
                placeholder="Select your role"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="form-department">Department</Label>
              <Select
                options={[
                  { value: "engineering", label: "Engineering" },
                  { value: "design", label: "Design" },
                  { value: "marketing", label: "Marketing" },
                  { value: "sales", label: "Sales" },
                ]}
                placeholder="Select department"
              />
            </div>
          </form>
        </ComponentPreview>
      </ComponentSection>
    </ComponentPage>
  )
}
